// Config.js — admin panel for organisations.configurables
//
// Lets you see and edit every configurable key catalog'd in CONFIG_CATALOG
// on the backend (fridge.in_range_pct, cleaning.on_time_pct, etc).
//
// Three actions per key:
//   1. "Apply defaults to all orgs" (top-level, one click sets every missing
//      key on every organisation to its catalog default)
//   2. Edit org-wide value (each key row)
//   3. Edit per-pharmacy override (expandable section per row)
//
// Backend endpoints:
//   GET    /api/headoffice/configurables
//   PUT    /api/headoffice/configurables/:key           body { value, organisationId? }
//   DELETE /api/headoffice/configurables/:key           body { organisationId? }
//   PUT    /api/headoffice/configurables/:pharmacyId/:key  body { value }
//   DELETE /api/headoffice/configurables/:pharmacyId/:key
//   POST   /api/headoffice/configurables/apply-defaults

import React, { useState, useEffect, useCallback } from "react";

import API_BASE from "./apiBase";

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
async function apiReq(method, path, body, token) {
  const opts = {
    method,
    headers: { Authorization: `Bearer ${token}` }
  };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}/api/headoffice/${path}`, opts);
  if (!res.ok) {
    const errText = await res.text().catch(() => "");
    throw new Error(`${path} → HTTP ${res.status} ${errText}`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Style tokens — match HeadOffice.js
// ---------------------------------------------------------------------------
const S = {
  wrap:     { padding: 16, fontFamily: "system-ui, sans-serif" },
  h1:       { fontSize: 22, fontWeight: 600, margin: "0 0 6px" },
  sub:      { fontSize: 13, color: "#6b7280", margin: "0 0 16px" },
  panel:    { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
              padding: 14, marginBottom: 16 },
  panelTitle:{ fontSize: 15, fontWeight: 600, marginBottom: 8 },
  table:    { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:       { textAlign: "left", borderBottom: "1px solid #d1d5db",
              padding: "8px", fontWeight: 600, color: "#374151",
              background: "#f9fafb" },
  td:       { borderBottom: "1px solid #f3f4f6", padding: "8px",
              color: "#1f2937", verticalAlign: "top" },
  btn:      { background: "#1976d2", color: "#fff", border: "none",
              borderRadius: 4, padding: "5px 10px", cursor: "pointer",
              fontSize: 12 },
  btnDanger:{ background: "#dc2626", color: "#fff", border: "none",
              borderRadius: 4, padding: "5px 10px", cursor: "pointer",
              fontSize: 12 },
  btnGhost: { background: "#f3f4f6", color: "#374151",
              border: "1px solid #d1d5db", borderRadius: 4,
              padding: "5px 10px", cursor: "pointer", fontSize: 12 },
  btnApply: { background: "#059669", color: "#fff", border: "none",
              borderRadius: 4, padding: "8px 14px", cursor: "pointer",
              fontSize: 13, fontWeight: 600 },
  input:    { width: 90, padding: "4px 6px", border: "1px solid #d1d5db",
              borderRadius: 4, fontSize: 13 },
  inputWide:{ width: 160, padding: "4px 6px", border: "1px solid #d1d5db",
              borderRadius: 4, fontSize: 13 },
  badge:    { display: "inline-block", padding: "2px 6px",
              fontSize: 11, borderRadius: 3, background: "#e5e7eb",
              color: "#374151", marginLeft: 6 },
  badgeSet: { display: "inline-block", padding: "2px 6px",
              fontSize: 11, borderRadius: 3, background: "#d1fae5",
              color: "#065f46", marginLeft: 6 },
  toast:    { position: "fixed", bottom: 20, right: 20,
              background: "#1f2937", color: "#fff", padding: "10px 14px",
              borderRadius: 4, fontSize: 13, zIndex: 1000 },
  error:    { color: "#b91c1c", fontSize: 13, padding: "4px 0" },
  expand:   { background: "#f9fafb", padding: 8, borderRadius: 4,
              marginTop: 6, fontSize: 12 }
};

// ---------------------------------------------------------------------------
// Single key row — org-level value + expandable per-pharmacy overrides
// ---------------------------------------------------------------------------
function KeyRow({ catalog, valueRow, org, token, onChange, onToast }) {
  const [orgInput, setOrgInput] = useState(
    valueRow.orgValue !== null ? String(valueRow.orgValue) : ""
  );
  const [expanded, setExpanded] = useState(false);

  const saveOrg = async () => {
    const val = catalog.type === "integer" ? parseInt(orgInput, 10) : Number(orgInput);
    if (!isFinite(val)) { onToast(`Invalid value for ${catalog.key}`); return; }
    try {
      await apiReq("PUT", `configurables/${catalog.key}`,
        { value: val, organisationId: org.organisationId }, token);
      onToast(`Saved ${catalog.key} = ${val}`);
      onChange();
    } catch (e) { onToast(e.message); }
  };

  const clearOrg = async () => {
    try {
      await apiReq("DELETE", `configurables/${catalog.key}`,
        { organisationId: org.organisationId }, token);
      onToast(`Cleared ${catalog.key} on ${org.organisationName}`);
      setOrgInput("");
      onChange();
    } catch (e) { onToast(e.message); }
  };

  const overrideCount = valueRow.members.filter(m => m.hasOverride).length;

  return (
    <tr>
      <td style={S.td}>
        <div style={{ fontWeight: 600 }}>{catalog.label}</div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
          <code>{catalog.key}</code> · default {catalog.default}{catalog.unit}
        </div>
        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>
          {catalog.description}
        </div>
        {overrideCount > 0 && (
          <button style={{ ...S.btnGhost, marginTop: 6 }}
                  onClick={() => setExpanded(!expanded)}>
            {expanded ? "Hide" : "Show"} {overrideCount} branch override{overrideCount === 1 ? "" : "s"}
          </button>
        )}
        {overrideCount === 0 && (
          <button style={{ ...S.btnGhost, marginTop: 6 }}
                  onClick={() => setExpanded(!expanded)}>
            {expanded ? "Hide" : "Add"} branch override
          </button>
        )}
      </td>
      <td style={S.td}>
        <span>
          {valueRow.hasOrgValue
            ? <>{valueRow.orgValue}{catalog.unit}<span style={S.badgeSet}>set</span></>
            : <>{catalog.default}{catalog.unit}<span style={S.badge}>using default</span></>}
        </span>
      </td>
      <td style={S.td}>
        <input
          type="number" style={S.input}
          value={orgInput}
          min={catalog.min} max={catalog.max}
          onChange={e => setOrgInput(e.target.value)}
        />{" "}
        <button style={S.btn} onClick={saveOrg}>Apply</button>{" "}
        {valueRow.hasOrgValue && (
          <button style={S.btnGhost} onClick={clearOrg}>Clear</button>
        )}
        {expanded && (
          <div style={S.expand}>
            <BranchOverrides
              catalog={catalog}
              members={valueRow.members}
              token={token}
              onChange={onChange}
              onToast={onToast}
            />
          </div>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Per-pharmacy override editor (shown when KeyRow is expanded)
// ---------------------------------------------------------------------------
function BranchOverrides({ catalog, members, token, onChange, onToast }) {
  const [drafts, setDrafts] = useState(
    Object.fromEntries(members.map(m => [
      String(m.pharmacyId || m.dbName),
      m.hasOverride ? String(m.value) : ""
    ]))
  );

  const save = async (m) => {
    const key = String(m.pharmacyId || m.dbName);
    const raw = drafts[key];
    const val = catalog.type === "integer" ? parseInt(raw, 10) : Number(raw);
    if (!isFinite(val)) { onToast("Invalid value"); return; }
    if (!m.pharmacyId) { onToast("No pharmacyId — can't write override"); return; }
    try {
      await apiReq("PUT",
        `configurables/${m.pharmacyId}/${catalog.key}`,
        { value: val }, token);
      onToast(`Override saved for ${m.pharmacyName}`);
      onChange();
    } catch (e) { onToast(e.message); }
  };

  const clear = async (m) => {
    if (!m.pharmacyId) return;
    try {
      await apiReq("DELETE",
        `configurables/${m.pharmacyId}/${catalog.key}`,
        undefined, token);
      onToast(`Override cleared for ${m.pharmacyName}`);
      onChange();
    } catch (e) { onToast(e.message); }
  };

  return (
    <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Pharmacy</th>
          <th style={S.th}>Override</th>
          <th style={S.th}>New value</th>
        </tr>
      </thead>
      <tbody>
        {members.map(m => {
          const key = String(m.pharmacyId || m.dbName);
          return (
            <tr key={key}>
              <td style={S.td}>{m.pharmacyName}</td>
              <td style={S.td}>
                {m.hasOverride
                  ? <><b>{m.value}{catalog.unit}</b><span style={S.badgeSet}>set</span></>
                  : <span style={{ color: "#9ca3af" }}>inherits org value</span>}
              </td>
              <td style={S.td}>
                <input
                  type="number" style={S.input}
                  value={drafts[key]}
                  min={catalog.min} max={catalog.max}
                  onChange={e => setDrafts({ ...drafts, [key]: e.target.value })}
                />{" "}
                <button style={S.btn} onClick={() => save(m)}>Apply</button>{" "}
                {m.hasOverride && (
                  <button style={S.btnGhost} onClick={() => clear(m)}>Clear</button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Main Config component
// ---------------------------------------------------------------------------
export default function Config({ token: tokenProp } = {}) {
  const [token] = useState(() =>
    tokenProp ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiReq("GET", "configurables", undefined, token);
      setData(result);
      if (!selectedOrgId && result.organisations.length > 0) {
        setSelectedOrgId(String(result.organisations[0].organisationId));
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token, selectedOrgId]);

  useEffect(() => { load(); }, [load]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const applyDefaults = async () => {
    if (!window.confirm(
      "Apply catalog defaults to every organisation? " +
      "Only sets keys that are currently missing — existing values are kept."
    )) return;
    try {
      const r = await apiReq("POST", "configurables/apply-defaults", {}, token);
      const totalSet = r.applied.reduce((a, b) => a + b.orgsSet, 0);
      showToast(`Applied: ${totalSet} (key × org) entries set`);
      load();
    } catch (e) { showToast(e.message); }
  };

  if (!token) {
    return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;
  }

  const selectedOrg = data?.organisations.find(
    o => String(o.organisationId) === String(selectedOrgId)
  );

  return (
    <div style={S.wrap}>
      <h1 style={S.h1}>Configurables</h1>
      <div style={S.sub}>
        Edit per-organisation thresholds and per-pharmacy overrides for the rules
        in <code>headoffice.js</code>. Missing values fall back to catalog
        defaults at runtime.
      </div>

      <div style={S.panel}>
        <div style={S.panelTitle}>Initial setup</div>
        <div style={{ fontSize: 13, color: "#374151", marginBottom: 8 }}>
          One-click migration — for every organisation, set any missing catalog
          key to its default. Safe to run repeatedly; existing values are never
          overwritten.
        </div>
        <button style={S.btnApply} onClick={applyDefaults}>
          Apply catalog defaults to all organisations
        </button>
      </div>

      {loading && <div>Loading…</div>}
      {error && <div style={S.error}>{error}</div>}

      {data && (
        <div style={S.panel}>
          <div style={S.panelTitle}>Organisation</div>
          <select
            value={selectedOrgId || ""}
            onChange={e => setSelectedOrgId(e.target.value)}
            style={{ ...S.inputWide, width: 280 }}
          >
            {data.organisations.map(o => (
              <option key={String(o.organisationId)} value={String(o.organisationId)}>
                {o.organisationName} ({o.values[0]?.members?.length || 0} branches)
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedOrg && (
        <div style={S.panel}>
          <div style={S.panelTitle}>{selectedOrg.organisationName} — configurables</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{ ...S.th, width: "40%" }}>Setting</th>
                <th style={{ ...S.th, width: "20%" }}>Current</th>
                <th style={S.th}>Apply new value</th>
              </tr>
            </thead>
            <tbody>
              {selectedOrg.values.map(v => (
                <KeyRow
                  key={v.key}
                  catalog={data.catalog.find(c => c.key === v.key)}
                  valueRow={v}
                  org={selectedOrg}
                  token={token}
                  onChange={load}
                  onToast={showToast}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}
